import axios from 'axios';

// ── Dynamic Server URL (for mobile app) ───────────────────
// On mobile/Capacitor, the user provides the server URL at login.
// On web (browser), it falls back to relative '/api' which works behind nginx/proxy.
function getBaseURL() {
  const stored = localStorage.getItem('ictops_server_url');
  if (stored) return stored;
  return process.env.REACT_APP_API_URL || '/api';
}

// ── Token management for mobile (cookies don't work cross-origin) ──
const TOKEN_KEY = 'ictops_auth_token';

function getStoredToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

export function setAuthToken(token) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

export function clearAuthToken() {
  localStorage.removeItem(TOKEN_KEY);
}

// Cookies are sent automatically by the browser (httpOnly cookie set by server).
// On mobile, we fall back to Bearer token in Authorization header.
const api = axios.create({
  baseURL:         getBaseURL(),
  withCredentials: true, // send cookies on every request (works for web)
  headers: {
    'X-Requested-With': 'XMLHttpRequest', // CSRF protection — required by server in production
  },
});

// ── Request interceptor: attach Bearer token for mobile ──
api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Response interceptor: handle 401 ──
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      // Cookie/token expired or invalid — redirect to login
      clearAuthToken();
      const publicPaths = ['/login', '/forgot-password', '/reset-password', '/accept-invite'];
      const isPublic = publicPaths.some(p => window.location.pathname.startsWith(p));
      if (!isPublic) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

/**
 * Update the API base URL at runtime (used when user enters server URL on mobile).
 * Persists to localStorage so it survives app restarts.
 */
export function setServerURL(url) {
  // Normalize: ensure it ends with /api if user provides just the domain
  let normalized = url.trim().replace(/\/+$/, '');
  if (!normalized.endsWith('/api')) {
    normalized += '/api';
  }
  localStorage.setItem('ictops_server_url', normalized);
  api.defaults.baseURL = normalized;
}

/**
 * Get the currently configured server URL (without /api suffix) for display.
 */
export function getServerURL() {
  const stored = localStorage.getItem('ictops_server_url');
  if (stored) return stored.replace(/\/api$/, '');
  return '';
}

/**
 * Clear the stored server URL (used for "change server" functionality).
 */
export function clearServerURL() {
  localStorage.removeItem('ictops_server_url');
  api.defaults.baseURL = process.env.REACT_APP_API_URL || '/api';
}

/**
 * Check if the app is running inside Capacitor (native mobile).
 */
export function isMobileApp() {
  return window.Capacitor !== undefined ||
         document.URL.startsWith('capacitor://') ||
         document.URL.startsWith('ionic://');
}

/**
 * Check if a server URL has been configured (needed for mobile).
 */
export function isServerConfigured() {
  return !!localStorage.getItem('ictops_server_url');
}

/**
 * Resolve a server-relative file path (e.g. an avatar stored as
 * "/uploads/avatars/x.jpg") into a URL the browser / mobile app can load.
 *
 * Uploaded files are served from the server ORIGIN at /uploads/... — NOT under
 * the /api base path — so we resolve against the API origin with the trailing
 * /api stripped. On web with a relative '/api' base this yields a same-origin
 * '/uploads/...' path; on mobile (full stored server URL) it yields an
 * absolute URL so the image loads from the right host.
 */
export function resolveFileUrl(filePath) {
  if (!filePath) return '';
  // Already absolute (http/https/data) — use as-is.
  if (/^(https?:|data:)/i.test(filePath)) return filePath;

  const base = api.defaults.baseURL || '/api';
  // Relative base ('/api') → serve from same origin.
  if (base.startsWith('/')) return filePath;
  // Absolute base (mobile) → strip trailing /api and join.
  const origin = base.replace(/\/api\/?$/, '');
  return `${origin}${filePath.startsWith('/') ? '' : '/'}${filePath}`;
}

export default api;
